/**
 * F27 (Wave 2E) — WhatsApp cost summary card (Decision D13).
 *
 * Mounts on the admin DashboardHome `/`. Shows the rolling-30d aggregation
 * of WhatsApp send volume + estimated cost + per-category + per-template
 * breakdown + opted-in user count + monthly budget threshold.
 *
 * Read endpoint: GET /admin/whatsapp/cost-summary (gated on `whatsapp:read`).
 * Renders nothing if the admin actor lacks the permission — the page-level
 * RBAC matrix is enforced server-side; this component degrades silently
 * when the read endpoint returns 403 (the catch keeps the dashboard usable
 * for content_moderator who DOES have whatsapp:read but may not always
 * have the perm in the future).
 *
 * Risk R16: FakeWhatsAppProvider sends show up here in local dev — this is
 * known + accepted. The card label includes "Estimated" so off-by-one cent
 * rounding is not a concern.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Card, CurrencyDisplay, Skeleton, Stat } from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import { getWhatsAppCostSummary, type CostSummaryResponse } from '../../api/whatsapp';

export function WhatsAppCostSummaryCard() {
  const { t, locale } = useI18n();
  const [data, setData] = useState<CostSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    getWhatsAppCostSummary()
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch(() => {
        if (cancelled) return;
        // Silently degrade — caller (DashboardHome) may render this card
        // for an admin without whatsapp:read; the dashboard stays useful.
        setErrored(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide the card entirely on 403 / network error so we never render an
  // empty error block to admins without the perm.
  if (errored) return null;

  if (loading) {
    return (
      <Card>
        <div className="flex flex-col gap-3" data-testid="whatsapp-cost-summary-loading">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </Card>
    );
  }

  if (!data || data.totalMessageCount === 0) {
    return (
      <Card>
        <div className="flex flex-col gap-3" data-testid="whatsapp-cost-summary-empty">
          <div className="flex items-center gap-2">
            <UIIcon name="bell" size={18} color="var(--color-info)" />
            <h3 className="text-[15px] font-bold text-ink">
              {t('admin:whatsapp.cost.card_title', {
                defaultValue: 'WhatsApp cost (rolling 30 days)',
              })}
            </h3>
          </div>
          <p className="text-[12px] text-ink-3">
            {t('admin:whatsapp.cost.empty_state', {
              defaultValue: 'No WhatsApp deliveries in the rolling 30-day window yet.',
            })}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-col gap-4" data-testid="whatsapp-cost-summary-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <UIIcon name="bell" size={18} color="var(--color-info)" />
            <h3 className="text-[15px] font-bold text-ink">
              {t('admin:whatsapp.cost.card_title', {
                defaultValue: 'WhatsApp cost (rolling 30 days)',
              })}
            </h3>
          </div>
        </div>

        <p className="text-[12px] text-ink-3">
          {t('admin:whatsapp.cost.card_description', {
            defaultValue:
              "Approximate cost estimate from Meta's Cloud API pricing. Exact billing comes from the Meta Business Manager dashboard.",
          })}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat
            label={t('admin:whatsapp.cost.total_message_count_label', {
              defaultValue: 'Messages delivered',
            })}
            value={String(data.totalMessageCount)}
          />
          <Stat
            label={t('admin:whatsapp.cost.estimated_cost_label', {
              defaultValue: 'Estimated cost',
            })}
            value={
              <CurrencyDisplay amount={data.estimatedCostUsdCents} currency="USD" locale={locale} />
            }
            data-testid="whatsapp-cost-estimated-stat"
          />
          <Stat
            label={t('admin:whatsapp.cost.monthly_budget_label', {
              defaultValue: 'Monthly budget',
            })}
            value={
              <CurrencyDisplay amount={data.monthlyBudgetUsdCents} currency="USD" locale={locale} />
            }
          />
          <Stat
            label={t('admin:whatsapp.cost.opted_in_user_count_label', {
              defaultValue: 'Opted-in users',
            })}
            value={String(data.optedInUserCount)}
          />
        </div>

        {data.byCategory.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-ink-3">
              {t('admin:whatsapp.cost.by_category_title', { defaultValue: 'By category' })}
            </div>
            <ul className="flex flex-col divide-y divide-hairline">
              {data.byCategory.map((b) => (
                <li
                  key={b.category}
                  className="flex justify-between items-center py-1.5 text-[12px]"
                  data-testid={`whatsapp-cost-category-${b.category}`}
                >
                  <span className="text-ink-2">
                    {t(`admin:whatsapp.templates.category.${b.category}`, {
                      defaultValue: b.category,
                    })}
                  </span>
                  <span className="text-ink font-mono tabular-nums">{b.count}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {data.byTemplate.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-ink-3">
              {t('admin:whatsapp.cost.by_template_title', { defaultValue: 'By template' })}
            </div>
            <ul className="flex flex-col divide-y divide-hairline">
              {data.byTemplate.map((b) => (
                <li
                  key={b.templateName}
                  className="flex justify-between items-center py-1.5 text-[12px]"
                  data-testid={`whatsapp-cost-template-${b.templateName}`}
                >
                  <span className="font-mono text-ink-2">{b.templateName}</span>
                  <span className="text-ink font-mono tabular-nums">{b.count}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export default WhatsAppCostSummaryCard;
